import { useMemo } from "react";
import { useEdges } from "@xyflow/react";

/**
 * Hook que retorna uma funcao para verificar se um handle esta conectado.
 * Uso: const getHandleClass = useHandleConnected(nodeId);
 *      <Handle className={getHandleClass("source", "a")} ... />
 *
 * Quando targetHandle/sourceHandle eh null na edge, o React Flow conecta
 * em qualquer handle disponivel. O hook replica esse comportamento usando
 * wildcards: se a edge nao especifica handle, TODOS os handles daquele
 * tipo (source/target) sao marcados como conectados.
 */
export default function useHandleConnected(nodeId) {
  const edges = useEdges();

  const connectedHandles = useMemo(() => {
    const connected = new Set();
    edges.forEach((edge) => {
      if (edge.source === nodeId) {
        if (edge.sourceHandle) {
          connected.add(`source-${edge.sourceHandle}`);
        } else {
          // null/undefined sourceHandle = React Flow conecta em qualquer source
          connected.add("source-*");
        }
      }
      if (edge.target === nodeId) {
        if (edge.targetHandle) {
          connected.add(`target-${edge.targetHandle}`);
        } else {
          // null/undefined targetHandle = React Flow conecta em qualquer target
          connected.add("target-*");
        }
      }
    });
    return connected;
  }, [edges, nodeId]);

  return (type, handleId) => {
    // Checa match exato OU wildcard
    const exactKey = `${type}-${handleId || ""}`;
    const wildcard = `${type}-*`;
    return (connectedHandles.has(exactKey) || connectedHandles.has(wildcard))
      ? "handle-connected"
      : "handle-disconnected";
  };
}
